import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, PhoneCall, Send, Loader2, Sparkles, XCircle, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Every button gets the dark base + gold accent brand treatment. Semantic
// tone is preserved via a coloured icon + border edge (not a flat fill) so
// the console still looks like NEX — never like a generic form.
const TONE = {
  success: { accent: "text-emerald-300", edge: "border-emerald-400/50", Icon: CheckCircle2 },
  neutral: { accent: "text-nex-silver",   edge: "border-nex-silver/40", Icon: Eye },
  urgent:  { accent: "text-red-300",      edge: "border-red-400/50",    Icon: PhoneCall },
  gold:    { accent: "text-nex-gold",     edge: "border-nex-gold/60",   Icon: Sparkles },
  muted:   { accent: "text-nex-chrome",   edge: "border-nex-border",    Icon: XCircle },
};

const ResponsePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(null); // response_key currently being submitted
  const [done, setDone] = useState(null); // { label, custom }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/r/${token}`);
        setData(data);
        if (data.responded) setDone({ label: data.response_label, custom: data.response_custom });
      } catch (err) {
        setError(err?.response?.data?.detail || "This link is invalid.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (response_key = null) => {
    setSubmitting(response_key || "__custom__");
    try {
      const { data } = await axios.post(`${API}/public/r/${token}/respond`, {
        response_key,
        custom_text: custom || null,
      });
      setDone({
        label: data.response_label,
        custom: custom || null,
        moved: data.moved_to_call_required,
      });
    } catch (err) {
      alert(err?.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(null); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
      <Loader2 className="animate-spin text-nex-gold" size={22} />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-[#0B0F19] flex flex-col items-center justify-center px-6 text-center">
      <div className="mono-body text-[13px] tracking-[0.5em] uppercase text-nex-gold font-bold mb-4">THE EXCHANGE ALLIANCE</div>
      <div className="mono-label !text-red-300 mb-2">// LINK ERROR</div>
      <p className="text-nex-chrome max-w-md">{error}</p>
    </div>
  );

  const firstName = data?.contact?.first_name || "there";

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="response-page">
      {/* Bold branded header — dark base, gold wordmark, gold hairline */}
      <div className="border-b-2 border-nex-gold/50 bg-black">
        <div className="max-w-md mx-auto px-5 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-nex-gold/60 flex items-center justify-center">
              <Sparkles size={14} className="text-nex-gold" />
            </div>
            <div>
              <div className="mono-body text-[13px] tracking-[0.5em] uppercase text-nex-gold font-bold leading-none">
                THE EXCHANGE ALLIANCE
              </div>
              <div className="mono-body text-[8px] tracking-[0.3em] uppercase text-nex-chrome mt-1">
                Custom Business Engines
              </div>
            </div>
          </div>
          <div className="mono-body text-[9px] tracking-[0.25em] uppercase text-nex-muted">
            One-tap · secure
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 pt-8 pb-14">
        {done ? (
          <div className="card-industrial corner-tick p-6 text-center" data-testid="response-done">
            <CheckCircle2 className="text-emerald-300 mx-auto mb-3" size={36} />
            <div className="mono-label mb-2">// RESPONSE RECEIVED</div>
            <h1 className="serif-head text-2xl mb-3">Thanks, {firstName}.</h1>
            <div className="border border-nex-gold/40 bg-nex-gold/5 p-4 mb-4">
              <div className="text-nex-gold font-bold text-lg">{done.label}</div>
              {done.custom && <div className="text-nex-chrome text-sm mt-2 whitespace-pre-wrap">{done.custom}</div>}
            </div>
            <p className="text-nex-chrome text-sm">
              {done.moved
                ? "Your callback request has been flagged — William or Robert will reach out shortly."
                : "The Exchange Alliance team has been notified by email and text."}
            </p>
          </div>
        ) : (
          <>
            <div className="mono-label mb-2">// FOR</div>
            <h1 className="serif-head text-3xl mb-2">{firstName}</h1>
            <p className="text-nex-chrome text-sm mb-6">{data.prompt}</p>

            <div className="space-y-3 mb-6" data-testid="response-buttons">
              {data.presets.map((p) => {
                const t = TONE[p.tone] || TONE.muted;
                const { Icon } = t;
                const busy = submitting === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => submit(p.key)}
                    disabled={!!submitting}
                    data-testid={`response-${p.key}`}
                    className={`w-full py-4 px-5 flex items-center justify-between gap-3 mono-body text-[13px] uppercase tracking-[0.15em] font-bold transition-all bg-black/60 border-l-4 ${t.edge} hover:bg-nex-gold/10 hover:border-nex-gold text-nex-silver disabled:opacity-60`}
                  >
                    <span className={`text-left ${t.accent}`}>{p.label}</span>
                    {busy
                      ? <Loader2 size={18} className="animate-spin text-nex-gold" />
                      : <Icon size={18} className={t.accent} />}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-nex-border pt-6">
              <div className="mono-label !text-[9px] mb-2">Or write a custom message</div>
              <textarea
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Type here…"
                className="input-industrial w-full mb-3"
                data-testid="response-custom"
              />
              <button
                onClick={() => submit(null)}
                disabled={!!submitting || !custom.trim()}
                data-testid="response-custom-send"
                className="w-full py-3 tick-btn-primary justify-center disabled:opacity-40"
              >
                {submitting === "__custom__" ? <><Loader2 size={14} className="animate-spin" /> Sending</> : <>Send message <Send size={14} /></>}
              </button>
            </div>

            <div className="mono-body text-[10px] text-nex-muted text-center mt-6">
              Personal link · secure · theexchangealliance.org
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResponsePage;
